if (!customElements.get('sizing-chart')) {
  customElements.define(
    'sizing-chart',
    class SizingChart extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.classes = {
          barSelected: 'sizing-chart__bar--selected',
          hidden : 'hidden',
        };

        this.container = this.closest('tabs-component');
        this.variantChangeUnsubscriber = null;
        this.chartBars = this.querySelectorAll('[data-sizing-chart-bar]');
        this.chartBarsArray = Array.from(this.chartBars);

        this.markerElem = this.querySelector('[data-sizing-chart-marker]');
        this.markerHeightElem = this.querySelector('[data-sizing-chart-marker-height]');
        this.chartMinRange = parseFloat(this.dataset.sizingChartMinRange || 0);
        this.chartMaxRange = parseFloat(this.dataset.sizingChartMaxRange || 0);
        this.chartColumnsCount = parseInt(this.dataset.sizingChartColumnsCount || 0);

        this.selectedSizeElem = this.closest('.tabs__content')?.querySelector('[data-sizing-chart-selected-size]');

        this.mobileQuery = window.matchMedia('(max-width: 767.98px)');
        this.isMobile = this.mobileQuery.matches;

        this.boundHandleManualScroll = this.handleManualScroll.bind(this);

        this.setupVariantChangeListener();
        this.setupBarClickListeners();
        this.setupProductUIUpdateListener();

        this.updateUserHeightMarker();

        window.addEventListener('recommended-size-updated', () => {
          this.updateUserHeightMarker();
        });

        this.mobileQuery.addEventListener('change', () => {
          this.isMobile = this.mobileQuery.matches;
          this.updateUserHeightMarker();
        });

        window.addEventListener('scroll', this.boundHandleManualScroll, { passive: true });
      }

      setActiveBar(bar, dispatchVariantChanged = false) {
        if (!bar) return;

        // Skip if bar is already selected
        if (bar.classList.contains(this.classes.barSelected)) return;

        const activeBar = this.chartBarsArray.find(chartBar => chartBar.classList.contains(this.classes.barSelected));
        const activeBarIcon = this.querySelector('[data-sizing-chart-bar-icon]');

        bar.classList.add(this.classes.barSelected);

        activeBar?.classList.remove(this.classes.barSelected);

        if (activeBarIcon) {
          bar.insertAdjacentHTML('beforeend', activeBarIcon.outerHTML);
          activeBarIcon.remove();
        }

        // Sync chart selection with main variant selector if needed
        if (dispatchVariantChanged) {
          const variantId = bar.dataset?.variantId;
          if (!variantId) return;

          // Sync chart selection with main variant selector
          const variantSelects = document.querySelector('variant-selects');
          if (!variantSelects) return;

          const variantInput = variantSelects.querySelector(`input[data-variant-id="${variantId}"]`);

          if (!variantInput || variantInput.checked) return;

          variantInput.checked = true;
          variantInput.dispatchEvent(new CustomEvent('change', {
            bubbles: true,
            detail: { source: 'sizing-chart' }
          }));
        }
      }

      setupBarClickListeners() {
        this.chartBars?.forEach(bar => {
          bar.addEventListener('click', () => {
            // Disable browser scroll restoration and store container position before variant change
            if ('scrollRestoration' in history) {
              history.scrollRestoration = 'manual';
            }

            this.containerTop = this.container?.getBoundingClientRect().top;
            this.originalScrollY = window.scrollY;
            this.userHasScrolled = false;
            this.isScrollLockActive = true;

            this.setActiveBar(bar, true);

            // Safety timeout: re-enable scroll restoration after 2 seconds if event never fires
            if (this.scrollRestorationTimeout) {
              clearTimeout(this.scrollRestorationTimeout);
            }

            this.scrollRestorationTimeout = setTimeout(() => {
              this.cleanupScrollLock();
            }, 2000);
          });
        });
      }

      handleManualScroll() {
        // Only track if scroll lock is active
        if (this.isScrollLockActive && this.originalScrollY !== undefined) {
          if (Math.abs(window.scrollY - this.originalScrollY) > 25) {
            this.userHasScrolled = true;
            this.cleanupScrollLock();
          }
        }
      }

      cleanupScrollLock() {
        this.isScrollLockActive = false;
        this.originalScrollY = undefined;
        if ('scrollRestoration' in history) {
          history.scrollRestoration = 'auto';
        }
      }

      setupProductUIUpdateListener() {
        document.addEventListener('product-UI-updated', () => {
          // Clear the safety timeout since event fired
          if (this.scrollRestorationTimeout) {
            clearTimeout(this.scrollRestorationTimeout);
            this.scrollRestorationTimeout = null;
          }

          requestAnimationFrame(() => {
            // Only restore scroll if user hasn't manually scrolled
            if (this.containerTop && !this.userHasScrolled) {
              const containerTopNew = this.container?.getBoundingClientRect().top;
              const shift = containerTopNew - this.containerTop;

              // Compensate for content shift
              if (Math.abs(shift) > 5) {
                window.scrollBy(0, shift);
              }
            }

            // Cleanup scroll listener
            this.cleanupScrollLock();
          });
        });
      }

      setupVariantChangeListener() {
        if (window.FoxTheme?.pubsub?.subscribe) {
          this.variantChangeUnsubscriber = window.FoxTheme.pubsub.subscribe(
            window.FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
            (event) => {
              const selectedVariantId = event.data?.variant?.id;
              if (!selectedVariantId) return;

              const bar = this.chartBarsArray.find(chartBar => Number(chartBar.dataset?.variantId) === selectedVariantId);

              this.setActiveBar(bar, false);

              this.setSelectedSize(event.data?.variant?.title);
            }
          );
        }
      }

      setSelectedSize(size) {
        if (!this.selectedSizeElem || !size) return;
        this.selectedSizeElem.textContent = size;
      }

      loadSizingMeasurements() {
        const STORAGE_KEY = 'bikeSizingData';

        try {
          const rawData = localStorage.getItem(STORAGE_KEY);
          const data = JSON.parse(rawData || '{}');

          if (data && (data.adjustedHeight || data.height)) {
            return {
              height: data.height ? parseFloat(data.height) : null,
              adjustedHeight: data.adjustedHeight ? parseFloat(data.adjustedHeight) : null,
            };
          } else {
            return null;
          }
        } catch (e) {
          console.error('[SizingChart] Error loading from local storage:', e);
          return null;
        }
      }

      calculateMarkerPosition(userHeight) {
        if (isNaN(this.chartMinRange) || isNaN(this.chartMaxRange) || isNaN(this.chartColumnsCount) || this.chartColumnsCount === 0) {
          return null;
        }

        if (userHeight < this.chartMinRange || userHeight > this.chartMaxRange) {
          return null;
        }

        // Height ratio (0 - 1)
        const heightRatio = (userHeight - this.chartMinRange) / (this.chartMaxRange - this.chartMinRange);

        if (this.isMobile) {
          // Mobile: vertical layout, position from top
          const chartHeaderRowHeight = parseFloat(getComputedStyle(this).getPropertyValue('--size-chart-header-row-height')) || 36;

          const chartHeight = this.offsetHeight - chartHeaderRowHeight;

          // Calculate position from the top
          const positionFromTop = heightRatio * chartHeight + chartHeaderRowHeight;

          return {
            axis: 'top',
            value: (positionFromTop / this.offsetHeight) * 100
          };
        }

        // Desktop: horizontal layout, position from left
        const labelWidth = parseFloat(getComputedStyle(this).getPropertyValue('--sizing-chart-label-width')) || 90;
        const chartWidth = this.offsetWidth - labelWidth;

        // Calculate position within the bar-container area (excluding label column) in pixels
        const positionLeft = heightRatio * chartWidth + labelWidth;

        // Convert position to percentage
        return {
          axis: 'left',
          value: (positionLeft / this.offsetWidth) * 100
        };
      }

      updateUserHeightMarker() {
        if (!this.markerElem) {
          return;
        }

        const measurements = this.loadSizingMeasurements();

        // Hide marker if no valid data
        if (!measurements) {
          this.markerElem.classList.add(this.classes.hidden);
          return;
        }

        const userHeight = measurements.adjustedHeight ?? measurements.height;

        if (!userHeight) {
          this.markerElem.classList.add(this.classes.hidden);
          return;
        }

        // Calculate marker position
        const position = this.calculateMarkerPosition(userHeight);

        if (position === null) {
          this.markerElem.classList.add(this.classes.hidden);
          return;
        }

        // Update height value
        if (this.markerHeightElem) {
          this.markerHeightElem.textContent = userHeight.toFixed(1);
        }

        if (position.axis === 'top') {
          this.markerElem.style.left = '';
          this.markerElem.style.top = `${position.value}%`;
        } else {
          this.markerElem.style.top = '';
          this.markerElem.style.left = `${position.value}%`;
        }

        // Make marker visible
        this.markerElem.classList.remove(this.classes.hidden);
      }

      disconnectedCallback() {
        // Cleanup subscription
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        // Clear timeout for scroll restoration cleanup
        if (this.scrollRestorationTimeout) {
          clearTimeout(this.scrollRestorationTimeout);
        }

        // Remove scroll listener
        if (this.boundHandleManualScroll) {
          window.removeEventListener('scroll', this.boundHandleManualScroll);
        }

        // Cleanup scroll lock
        this.cleanupScrollLock();
      }
    }
  );
}
